import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FacebookShareButton } from 'react-share';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import { useHistory } from 'react-router';

import ShoppingBasketIcon from '../../../../../../../components/icons/ShoppingBasketIcon';
import StatusDot from '../../../../../../../components/global/StatusDot';
import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import { SHARE_BLOCK_MODAL } from '../../../../../../../constants/modals';
import getConfig from '../../../../../../../util/config';
import WarningIcon from '../../../../../../../components/icons/WarningIcon';
import DownloadIcon from '../../../../../../../components/icons/DownloadIcon';
import PhotoGridViewerActionButton from '../../../../../../../components/grid/components/PhotoGridViewerActionButton';
import usePhotoDownload from '../../../../../../../hooks/usePhotoDownload';
import useBasename from '../../../../../../../components/routing/useBasename';
import { canAccessOriginalDownloadsOnBasename } from '../../../../../../../util/product-permissions';
import FacebookSquareIcon from '../../../../../../../components/icons/FacebookSquareIcon';
import MatchedPhotoFavoriteButton from '../MatchedPhotoFavoriteButton/MatchedPhotoFavoriteButton';
import FavoritesIcon from '../../../../../../../components/icons/FavoritesIcon';
import FavoritesEmptyIcon from '../../../../../../../components/icons/FavoritesEmptyIcon';
import { WEB_GALLERY_DOWNLOAD_MODAL } from '../../../../../../web-gallery/components/WebGalleryDownloadModal/WebGalleryDownloadModal';
import { removeHashes } from '../../../../../../../util/text';
import SendIcon from '../../../../../../../components/icons/SendIcon';
import AddPrintPackageIcon from '../../../../../../../components/icons/AddPrintPackageIcon';
import ShutterbugPhotoGroupPhotoViewerActions from '../../../../Album/AlbumPhotos/components/ShutterbugPhotoGroupPhotoViewerActions';
import { isProContent } from '../../../../../../../util/album';
import DropdownMenu from '../../../../../../../components/global/DropdownMenu';
import Button from '../../../../../../../components/global/Button';
import { ALBUM_SUBTYPE } from '../../../../../../../constants/albums';
import useViewPermission from '../../../../../services/contributors/hooks/useViewPermission';

const propTypes = {
  matchedPhoto: PropTypes.shape({
    id: PropTypes.string.isRequired,
    photo: PropTypes.shape({
      id: PropTypes.string.isRequired,
      thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          width: PropTypes.number,
          height: PropTypes.number,
          size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
        }),
      ),
      share_blocked: PropTypes.bool,
    }).isRequired,
    favoritePhoto: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  canPurchasePhotoItems: PropTypes.bool.isRequired,
  canReportPhoto: PropTypes.bool,
  canDownloadPhoto: PropTypes.bool,
  canConfigureGreenScreenPhoto: PropTypes.bool,
  isEntitled: PropTypes.bool.isRequired,
  onPhotoItemPurchase: PropTypes.func.isRequired,
  hasCartItems: PropTypes.bool,
  onPhotoReport: PropTypes.func.isRequired,
  socialShareEnabled: PropTypes.bool,
  hashCode: PropTypes.string,
  openModal: PropTypes.func.isRequired,
  isAllPhotos: PropTypes.bool,
  onPhotoItemWaldogramPurchase: PropTypes.func,
  onPhotoItemCampergramPurchase: PropTypes.func,
  hasUnconfiguredPrePurchases: PropTypes.bool,
  onConfigurePrepaidItems: PropTypes.func,
  includeWatermark: PropTypes.bool,
  albumFolderId: PropTypes.string,
  hierarchyItemId: PropTypes.string,
  sellPhotosModeEnabled: PropTypes.bool,
};

const MatchedPhotoPhotoViewerActions = ({
  matchedPhoto,
  isEntitled,
  canPurchasePhotoItems,
  canReportPhoto,
  canDownloadPhoto = true,
  canConfigureGreenScreenPhoto,
  onPhotoItemPurchase,
  hasCartItems,
  onPhotoReport,
  socialShareEnabled,
  hashCode,
  openModal,
  isAllPhotos,
  onPhotoItemWaldogramPurchase,
  onPhotoItemCampergramPurchase,
  hasUnconfiguredPrePurchases,
  onConfigurePrepaidItems,
  includeWatermark,
  albumFolderId,
  hierarchyItemId,
  sellPhotosModeEnabled,
}) => {
  const history = useHistory();
  const { album, photo } = matchedPhoto;
  const { handleDownload, loading: loadingDownload } = usePhotoDownload({
    photoId: photo.id,
    imageSizeCode: isEntitled
      ? IMAGE_SIZE_CODE.LARGE2X
      : IMAGE_SIZE_CODE.MEDIUM,
    includeWatermark,
  });
  const basename = useBasename();
  const { isAllowed: isAllowedToView } = useViewPermission({
    entityId: album?.id,
  });
  const allowOriginalDownload =
    canAccessOriginalDownloadsOnBasename(basename) ||
    isAllowedToView ||
    (sellPhotosModeEnabled && isEntitled);
  const renderCampergramOption =
    !!onPhotoItemCampergramPurchase ||
    album?.album_subtype === ALBUM_SUBTYPE.CAMPS;

  if (!isProContent(album) && photo?.is_owner && (isEntitled || isAllPhotos)) {
    return (
      <MatchedPhotoFavoriteButton photo={photo} album={album}>
        {({ isFavorite, onFavoriteToggle, isDropDown }) => (
          <ShutterbugPhotoGroupPhotoViewerActions
            album={album}
            photo={photo}
            onFavoriteToggle={onFavoriteToggle}
            isFavorite={isFavorite}
            onPhotoItemPurchase={onPhotoItemPurchase}
            ignorePhotoStatus
            albumFolderId={albumFolderId}
            isDropDown={isDropDown}
            canDownloadPhoto={canDownloadPhoto}
          />
        )}
      </MatchedPhotoFavoriteButton>
    );
  }

  return (
    <>
      {canDownloadPhoto && (
        <Fragment>
          {isAllPhotos ? (
            <Fragment>
              <PhotoGridViewerActionButton
                label="Download"
                icon={<DownloadIcon />}
                onClick={() =>
                  openModal(WEB_GALLERY_DOWNLOAD_MODAL, {
                    photo,
                    album,
                    forceAllowHiResDownloads: isEntitled,
                  })
                }
              />
            </Fragment>
          ) : (
            <Fragment>
              {isEntitled &&
                (allowOriginalDownload ? (
                  <div className="dropdown-menu-dark">
                    <DropdownMenu
                      toggle={
                        <PhotoGridViewerActionButton
                          label="Download Photo"
                          icon={<DownloadIcon />}
                          disabled={loadingDownload}
                          className={classNames({
                            'is-loading': loadingDownload,
                          })}
                        />
                      }
                    >
                      <Button
                        btnStyle="reset"
                        className="dropdown-item h-auto-i"
                        disabled={loadingDownload}
                        onClick={() => handleDownload()}
                      >
                        <p className="mb-0">
                          Download hi-res
                          <br />
                          <span className="text-light text-xs">
                            Great for prints up to 8x10
                          </span>
                        </p>
                      </Button>
                      <Button
                        btnStyle="reset"
                        className="dropdown-item h-auto-i"
                        disabled={loadingDownload}
                        onClick={() =>
                          handleDownload({ downloadOriginal: true })
                        }
                      >
                        <p className="mb-0">
                          Download original
                          <br />
                          <span className="text-light text-xs">
                            Great for large format printing
                          </span>
                        </p>
                      </Button>
                    </DropdownMenu>
                  </div>
                ) : (
                  <PhotoGridViewerActionButton
                    label={
                      isEntitled ? 'Download High-res' : 'Download Low-res'
                    }
                    icon={<DownloadIcon />}
                    onClick={handleDownload}
                    disabled={loadingDownload}
                    className={classNames({
                      'is-loading': loadingDownload,
                    })}
                  />
                ))}
            </Fragment>
          )}
        </Fragment>
      )}
      {canConfigureGreenScreenPhoto && isEntitled && (
        <PhotoGridViewerActionButton
          label="Configure for download"
          icon={<DownloadIcon />}
          onClick={() =>
            history.push(
              `/cards/downloads-configurator/${
                hierarchyItemId ?? album?.id
              }?photoIds[]=${photo.id}`,
            )
          }
        />
      )}
      {!sellPhotosModeEnabled && (
        <Fragment>
          {(isAllPhotos || isEntitled) && (
            <MatchedPhotoFavoriteButton photo={photo} album={album}>
              {({ isFavorite, onFavoriteToggle }) => (
                <PhotoGridViewerActionButton
                  label={isFavorite ? 'Remove Favorite' : 'Favorite Photo'}
                  icon={
                    isFavorite ? (
                      <FavoritesIcon color="#fe5851" />
                    ) : (
                      <FavoritesEmptyIcon />
                    )
                  }
                  onClick={onFavoriteToggle}
                />
              )}
            </MatchedPhotoFavoriteButton>
          )}
        </Fragment>
      )}
      {!hasUnconfiguredPrePurchases && canPurchasePhotoItems && (
        <PhotoGridViewerActionButton
          label="Purchasing Options"
          icon={
            <>
              <ShoppingBasketIcon size={20} />
              {hasCartItems && (
                <StatusDot
                  className="photo-viewer-status-dot"
                  status="danger"
                  size="lg"
                />
              )}
            </>
          }
          onClick={onPhotoItemPurchase}
        />
      )}
      {!sellPhotosModeEnabled && (
        <Fragment>
          {isEntitled &&
            onPhotoItemWaldogramPurchase &&
            !renderCampergramOption && (
              <PhotoGridViewerActionButton
                label="Send a card"
                icon={
                  <>
                    <SendIcon size={20} />
                  </>
                }
                onClick={onPhotoItemWaldogramPurchase}
              />
            )}
          {isEntitled &&
            !onPhotoItemWaldogramPurchase &&
            onPhotoItemCampergramPurchase && (
              <PhotoGridViewerActionButton
                label="Send a card"
                icon={
                  <>
                    <SendIcon size={20} />
                  </>
                }
                onClick={onPhotoItemCampergramPurchase}
              />
            )}
          {isEntitled &&
            onPhotoItemWaldogramPurchase &&
            renderCampergramOption && (
              <div className="dropdown-menu-dark">
                <DropdownMenu
                  placement="top-start"
                  toggle={
                    <PhotoGridViewerActionButton
                      label="Send a card"
                      icon={
                        <>
                          <SendIcon size={20} />
                        </>
                      }
                    />
                  }
                  closeOnClickWithin
                >
                  <Button
                    btnStyle="reset"
                    className="dropdown-item"
                    onClick={onPhotoItemCampergramPurchase}
                    disabled={!onPhotoItemCampergramPurchase}
                  >
                    <p
                      className="mb-0"
                      data-tip=""
                      data-for={`${photo?.id}-campergram-disabled`}
                      data-effect="solid"
                    >
                      Send a card to your camper{' '}
                      {!onPhotoItemCampergramPurchase && (
                        <ReactTooltip
                          id={`${photo?.id}-campergram-disabled`}
                          place="bottom"
                          className="tooltip"
                        >
                          Camper postcards are not
                          <br />
                          available or no longer available
                          <br />
                          due to delivery time.
                        </ReactTooltip>
                      )}
                    </p>
                  </Button>
                  <Button
                    btnStyle="reset"
                    className="dropdown-item"
                    onClick={onPhotoItemWaldogramPurchase}
                  >
                    <p className="mb-0">Send a card to someone else</p>
                  </Button>
                </DropdownMenu>
              </div>
            )}
        </Fragment>
      )}
      {hasUnconfiguredPrePurchases && (
        <PhotoGridViewerActionButton
          label="Add to order"
          icon={
            <>
              <AddPrintPackageIcon size={20} />
            </>
          }
          onClick={onConfigurePrepaidItems}
        />
      )}
      {((socialShareEnabled && isEntitled) || canReportPhoto) && (
        <Fragment>
          {socialShareEnabled &&
            isEntitled &&
            (photo.share_blocked ? (
              <PhotoGridViewerActionButton
                label="Share on Facebook"
                icon={<FacebookSquareIcon />}
                onClick={() => openModal(SHARE_BLOCK_MODAL)}
              />
            ) : (
              <PhotoGridViewerActionButton>
                {({ isMobile: isMobileResolution, className }) => (
                  <FacebookShareButton
                    className={className}
                    url={`${getConfig().app.baseUrl}/p/${photo.id}`}
                    hashtag={hashCode && `#${removeHashes(hashCode)}`}
                    data-tip="Share on Facebook"
                    data-for="facebook-share-button"
                  >
                    <FacebookSquareIcon />
                    {isMobileResolution && (
                      <span className="ml-1">Share on Facebook</span>
                    )}
                    {!isMobileResolution && (
                      <ReactTooltip
                        id="facebook-share-button"
                        className="tooltip"
                      />
                    )}
                  </FacebookShareButton>
                )}
              </PhotoGridViewerActionButton>
            ))}

          {canReportPhoto && (
            <PhotoGridViewerActionButton
              label="Report this photo"
              icon={<WarningIcon />}
              onClick={() => onPhotoReport(photo.id)}
            />
          )}
        </Fragment>
      )}
    </>
  );
};

MatchedPhotoPhotoViewerActions.propTypes = propTypes;

export default MatchedPhotoPhotoViewerActions;
